import {html, LitElement, PropertyValues} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {repositoryService, bookNames, BookItem} from "./repository-service";
import '@lit-labs/virtualizer';

@customElement('app-bible')
export class Bible extends LitElement {
  createRenderRoot = () => this

  @property() version: string = 'acv';
  @property() book: string = 'Gen';

  private _items: BookItem[] = [];
  private _indexMap = new Map<string, number>();
  private _syncing = false;
  private _scrollDebounce: ReturnType<typeof setTimeout> | null = null;
  private _loading = false;
  private _header: Element | null = null;

  protected willUpdate(changed: PropertyValues) {
    if (changed.has('version') || changed.has('book')) {
      this._loadData();
    }
  }

  private async _loadData() {
    this._loading = true;
    this.requestUpdate();
    const [items, doc] = await Promise.all([
      repositoryService.getBookItems(this.version, this.book),
      repositoryService.getVersion(this.version)
    ]);
    this._items = items;
    this._header = doc?.querySelector('header') ?? null;
    this._indexMap.clear();
    this._items.forEach((item, i) => this._indexMap.set(item.osisID, i));
    this._loading = false;
    this.requestUpdate();

    // Scroll to top on book change
    await this.updateComplete;
    const virt = this.querySelector('lit-virtualizer') as any;
    if (virt) virt.scrollToIndex(0, 'start');
  }

  private _renderItem = (item: BookItem) => {
    if (item.type === 'chapter') {
      return html`<div class=${classMap({'chapter-break': true, 'first': item.label === '1'})}><h3>Chapter ${item.label}</h3></div>`;
    }
    return html`<app-bible-verse
      .osisID=${item.osisID}
      .verseNum=${item.verseNum}
      .verseHtml=${item.html}
    ></app-bible-verse>`;
  }

  private _onVisibilityChanged = (e: any) => {
    if (this._syncing) return;
    const first = e.first;
    if (first == null || first >= this._items.length) return;

    if (this._scrollDebounce) clearTimeout(this._scrollDebounce);
    const capturedFirst = first;
    this._scrollDebounce = setTimeout(() => {
      let item = this._items[capturedFirst];
      if (item.type === 'chapter' && capturedFirst + 1 < this._items.length) {
        item = this._items[capturedFirst + 1];
      }
      this.dispatchEvent(new CustomEvent('pane-scroll', {
        bubbles: true, composed: true,
        detail: { osisID: item.osisID, source: this }
      }));
    }, 150);
  }

  scrollToVerse(osisID: string) {
    const index = this._indexMap.get(osisID);
    if (index == null) return;
    this._syncing = true;
    const virt = this.querySelector('lit-virtualizer') as any;
    if (virt) {
      virt.scrollToIndex(index, 'start');
    }
    setTimeout(() => this._syncing = false, 800);
  }

  render() {
    if (this._loading) {
      return html`<div class="loading">Loading...</div>`;
    }

    if (!this._items.length) {
      return html`<strong>Book not available in this version.</strong>`;
    }

    const name = bookNames[this.book] || this.book;

    return html`
      <app-bible-header .element=${this._header}></app-bible-header>
      <h2>${name}</h2>
      <lit-virtualizer
        scroller
        .items=${this._items}
        .renderItem=${this._renderItem}
        @visibilityChanged=${this._onVisibilityChanged}
      ></lit-virtualizer>
    `;
  }
}
